import { motion } from "framer-motion";
import AboutSubNav from "@/components/AboutSubNav";
import SEOHead from "@/components/SEOHead";
import BreadcrumbJsonLd from "@/components/BreadcrumbJsonLd";
import { GraduationCap, Lightbulb, Globe, Trophy, Quote, Linkedin, ArrowDown, ArrowRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import ScrollToTop from "@/components/ScrollToTop";
import Footer from "@/components/Footer";
import ContactCTA from "@/components/ContactCTA";
import { Button } from "@/components/ui/button";
import LatestNews from "@/components/LatestNews";
import boardHero from "@/assets/board/board-hero.jpg";
import adamSherlock from "@/assets/board/adam-sherlock.png";
import markEgerton from "@/assets/board/mark-egerton.png";
import boardSupport from "@/assets/board/board-support.png";
import boardGroupPhoto from "@/assets/board/board-group-photo.jpg";
import { useLanguage } from "@/hooks/useLanguage";
import { getBoardT } from "@/i18n/board";
import { localizeHref } from "@/lib/localizeHref";

/* ───────── advisor data ───────── */
type Advisor = {
  name: string; image: string; bio: string; quote: string; linkedin?: string;
  bioEs?: string; quoteEs?: string;
  bioZh?: string; quoteZh?: string;
};

const advisors: Advisor[] = [
  {
    name: "Adam Sherlock",
    image: adamSherlock,
    bio: "Adam has nearly 40 years of experience in the life sciences sector, leading professional services, technology, consultancy, and outsourcing companies. As CEO of multiple private equity-backed firms, he has driven remarkable growth and value creation.",
    bioEs: "Adam tiene casi 40 años de experiencia en el sector Life Sciences, liderando empresas de servicios profesionales, tecnología, consultoría y outsourcing. Como CEO de múltiples empresas respaldadas por capital privado, ha impulsado un crecimiento y una creación de valor notables.",
    bioZh: "Adam 在生命科学领域拥有近 40 年的经验,曾领导多家专业服务、技术、咨询与外包公司。作为多家私募股权支持企业的 CEO,他推动了卓越的增长与价值创造。",
    quote: "I'm honored to be part of QbD Group's exciting journey and look forward to helping them navigate new opportunities and challenges in the evolving life sciences market.",
    quoteEs: "Es un honor formar parte del emocionante camino de QbD Group y espero ayudarles a navegar nuevas oportunidades y retos en el cambiante mercado Life Sciences.",
    quoteZh: "我很荣幸能参与 QbD Group 这段令人振奋的旅程,期待协助他们在不断演变的生命科学市场中把握新机遇、应对新挑战。",
    linkedin: "https://www.linkedin.com/in/adamsherlock65/",
  },
  {
    name: "Mark Egerton",
    image: markEgerton,
    bio: "Mark brings more than 35 years of experience in pharmaceutical development and commercialization. As the former CEO of Quotient Sciences, he led an extraordinary phase of growth, significantly increasing revenue and shareholder value.",
    bioEs: "Mark aporta más de 35 años de experiencia en desarrollo y comercialización farmacéutica. Como ex CEO de Quotient Sciences, lideró una fase extraordinaria de crecimiento, incrementando significativamente los ingresos y el valor para los accionistas.",
    bioZh: "Mark 在制药开发与商业化方面拥有超过 35 年的经验。作为 Quotient Sciences 的前任 CEO,他领导了一段非凡的增长阶段,显著提升了收入与股东价值。",
    quote: "I'm excited to contribute to QbD Group's continued success, helping them turn their innovative solutions into real value for the global life sciences community.",
    quoteEs: "Me entusiasma contribuir al éxito continuo de QbD Group, ayudándoles a convertir sus soluciones innovadoras en valor real para la comunidad global de Life Sciences.",
    quoteZh: "我很高兴能为 QbD Group 的持续成功贡献力量,帮助他们将创新解决方案转化为对全球生命科学界的真正价值。",
    linkedin: "https://www.linkedin.com/in/markegerton/",
  },
];

const VALUE_ICONS = [GraduationCap, Lightbulb, Globe, Trophy];

/* ═══════════════════ MAIN PAGE ═══════════════════ */
const BoardOfAdvisors = () => {
  const lang = useLanguage();
  const t = getBoardT(lang);
  const aboutPath = lang === "es" ? "/es/sobre-qbd" : "/en/about-qbd";
  const homePath = localizeHref("/", lang);
  const homeLabel = lang === "es" ? "Inicio" : lang === "zh-cn" ? "首页" : "Home";

  return (
    <>
      <SEOHead
        title={t.seo.title}
        description={t.seo.description}
        canonical={`https://qbdgroup.com${lang === "es" ? "/es/sobre-qbd/consejo-asesor" : "/en/about-qbd/board-of-advisors"}`}
        ogImage={boardHero}
      />
      <BreadcrumbJsonLd items={[{ name: t.breadcrumb.about, path: aboutPath }, { name: t.breadcrumb.board }]} />

      <ScrollToTop />
      <Navbar />

      <main>
        {/* ── HERO ── */}
        <section className="relative overflow-hidden bg-primary pt-36 sm:pt-44 pb-20 sm:pb-28 min-h-[420px] sm:min-h-[480px]">
          <div className="absolute inset-0">
            <img src={boardHero} alt="" className="h-full w-full object-cover" aria-hidden="true" />
            <div className="absolute inset-0 bg-primary/80" />
          </div>

          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="max-w-2xl">
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                <nav className="mb-6 flex items-center gap-2 text-sm text-primary-foreground/50">
                  <a href={homePath} className="hover:text-primary-foreground/70 transition-colors">{homeLabel}</a>
                  <span>/</span>
                  <a href={aboutPath} className="hover:text-primary-foreground/70 transition-colors">{t.breadcrumb.about}</a>
                  <span>/</span>
                  <span className="text-primary-foreground/80">{t.breadcrumb.board}</span>
                </nav>

                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-primary-foreground leading-[1.1]">
                  {t.hero.titlePre}{" "}
                  <span className="text-accent">{t.hero.titleAccent}</span>
                </h1>
                <p className="mt-6 text-base sm:text-lg leading-relaxed text-primary-foreground/75 max-w-xl">
                  {t.hero.subtitle}
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <a href="#advisors">
                    <Button size="lg" className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90 shadow-lg px-8">
                      {t.hero.ctaAdvisors} <ArrowDown size={16} className="ml-2" />
                    </Button>
                  </a>
                  <a href="#value">
                    <Button size="lg" variant="outline" className="rounded-full border-primary-foreground/40 text-primary-foreground bg-primary-foreground/10 hover:bg-primary-foreground/20 px-8">
                      {t.hero.ctaValue} <ArrowRight size={16} className="ml-2" />
                    </Button>
                  </a>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        <AboutSubNav />

        {/* ── WHY VALUABLE ── */}
        <section id="value" className="py-20 sm:py-28 bg-background scroll-mt-32">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="text-center mb-14">
              <p className="text-sm font-semibold uppercase tracking-widest text-accent mb-3">{t.value.kicker}</p>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-foreground">{t.value.title}</h2>
              <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">{t.value.subtitle}</p>
            </motion.div>

            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {t.value.items.map((v, i) => {
                const Icon = VALUE_ICONS[i];
                return (
                  <motion.div key={v.title} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.4, delay: i * 0.08 }} whileHover={{ y: -5, scale: 1.02 }} className="rounded-2xl border border-border/60 bg-card p-7 shadow-sm transition-shadow hover:shadow-lg hover:shadow-accent/10">
                    <Icon size={28} className="text-accent mb-4" />
                    <h3 className="text-lg font-bold text-foreground mb-2">{v.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{v.desc}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── HOW THE BOARD SUPPORTS ── */}
        <section className="py-20 sm:py-28 bg-muted/60">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid gap-12 lg:grid-cols-2 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
              <p className="text-sm font-semibold uppercase tracking-widest text-accent mb-3">{t.support.kicker}</p>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-foreground mb-6">{t.support.title}</h2>
              <p className="text-muted-foreground leading-relaxed mb-4" dangerouslySetInnerHTML={{ __html: t.support.p1 }} />
              <p className="text-muted-foreground leading-relaxed mb-6">{t.support.p2}</p>
              <ul className="space-y-3 text-muted-foreground text-sm">
                {t.support.bullets.map((b, i) => (
                  <li key={i} className="flex gap-2"><span className="text-accent mt-0.5">•</span>{b}</li>
                ))}
              </ul>
            </motion.div>
            <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
              <img src={boardSupport} alt="Board of Advisors" className="rounded-2xl shadow-lg w-full max-h-[440px] object-cover" />
            </motion.div>
          </div>
        </section>

        {/* ── ADVISOR PROFILES ── */}
        <section id="advisors" className="py-20 sm:py-28 bg-primary scroll-mt-32">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="text-center mb-14">
              <p className="text-sm font-semibold uppercase tracking-widest text-accent mb-3">{t.profiles.kicker}</p>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-primary-foreground">{t.profiles.title}</h2>
              <p className="mt-4 text-primary-foreground/60 max-w-2xl mx-auto">{t.profiles.subtitle}</p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
              {advisors.map((a, i) => (
                <motion.div key={a.name} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.5, delay: i * 0.1 }} className="rounded-2xl bg-primary-foreground/[0.06] border border-primary-foreground/10 backdrop-blur-sm overflow-hidden">
                  <div className="flex items-center gap-5 p-6 pb-0">
                    <img src={a.image} alt={a.name} className="w-28 h-28 rounded-full object-cover ring-2 ring-accent/30 shrink-0" loading="lazy" />
                     <div>
                       <h3 className="text-lg font-bold text-primary-foreground">{a.name}</h3>
                       <p className="text-sm text-accent font-medium">{t.profiles.advisorRole}</p>
                       {a.linkedin && (
                         <a href={a.linkedin} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 mt-4 text-xs text-primary-foreground/50 hover:text-accent transition-colors" aria-label={`${a.name} on LinkedIn`}>
                           <Linkedin size={16} className="text-current" />
                         </a>
                       )}
                    </div>
                  </div>
                  <div className="px-6 pt-4">
                    <p className="text-sm text-primary-foreground/60 leading-relaxed">{lang === "zh-cn" && a.bioZh ? a.bioZh : lang === "es" && a.bioEs ? a.bioEs : a.bio}</p>
                  </div>
                  <div className="mx-6 mt-5 mb-6 rounded-xl bg-primary-foreground/[0.05] p-4 flex items-start gap-3">
                    <Quote size={16} className="text-accent shrink-0 mt-0.5" />
                    <p className="text-sm italic text-primary-foreground/70 leading-relaxed">"{lang === "zh-cn" && a.quoteZh ? a.quoteZh : lang === "es" && a.quoteEs ? a.quoteEs : a.quote}"</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ── GROUP PHOTO ── */}
        <section className="relative">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <img src={boardGroupPhoto} alt="Board of Advisors group photo" className="w-full aspect-[21/9] object-cover" loading="lazy" />
          </motion.div>
        </section>

        <LatestNews />
        <ContactCTA />
      </main>

      <Footer />
    </>
  );
};

export default BoardOfAdvisors;
